import { useStytch } from "@louise/api/useStytch";
import { getIdsFromMetadata, useApiClient } from "@louise/api/louise-api";
import { useTranslations } from "@src/packages/i18n";
import type { ResponseBody } from "@src/packages/openapi/helpers";
import type { paths } from "@src/packages/openapi/schema";
import { getAiChatFixture } from "../dev/aiChatFixture";
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

const API_BASE_URL = process.env.EXPO_PUBLIC_API_BASE_URL!;
const CHAT_SESSION_TIMEOUT_MS = 15_000;
const CHAT_RESPONSE_TIMEOUT_MS = 30_000;

export type AiConversationMessage =
  | { role: "user"; text: string; quote?: string }
  | {
      role: "assistant";
      thinking: boolean;
      response: AiChatResponse | null;
    };

export type AiChatSendOptions = {
  actionItemId?: string;
  actionItemContext?: string;
  quote?: string;
};

interface ChatSession {
  chatId: string;
  websocketUrl?: string;
}

type AiChatDetail = ResponseBody<paths["/ai/chats/{chatId}"]["get"]>["data"];

interface PendingRequest {
  onStream?: (response: AiChatResponse) => void;
  reject: (error: Error) => void;
  resolve: (response: AiChatResponse) => void;
  timeoutId: ReturnType<typeof setTimeout>;
}

export interface AiSource {
  id: string;
  label: string;
  source_type: string;
  organization?: string;
  year?: number;
  is_clickable: boolean;
  url?: string;
  quality_hint?: string;
}

export interface ReasoningItem {
  id: string;
  label: string;
  value?: string;
  date?: string;
  status: "present" | "missing" | "uncertain";
}

export interface ReasoningCategory {
  key: string;
  label: string;
  items: ReasoningItem[];
}

export interface SimpleAnswerBlock {
  type: "simple_answer";
  title: string;
  content: string;
  sources: AiSource[];
}

export interface ReasoningBlock {
  type: "reasoning";
  title: string;
  summary: string;
  categories: ReasoningCategory[];
  sources: AiSource[];
}

export interface ActionPlanItem {
  id: string;
  kind:
    | "test"
    | "supplement"
    | "lifestyle"
    | "doctor_discussion"
    | "follow_up"
    | "other";
  label: string;
  status: "active" | "updated" | "superseded" | "removed";
  priority: "high" | "medium" | "low";
  recommendation_text: string;
  rationale: string;
  quality_of_evidence:
    "high" | "moderate" | "low" | "very_low" | "insufficient";
  confidence_note?: string;
  related_datapoints?: string[];
  related_sources?: string[];
  /** Short, patient-specific follow-up questions (≤12 words, max 3). Optional —
   *  may be absent or empty; rendered as tappable chips on the latest turn. */
  follow_ups?: string[];
  card?: {
    title: string;
    subtitle?: string;
    body: string;
  };
}

export interface ActionPlanBlock {
  type: "action_plan";
  title: string;
  summary: string;
  items: ActionPlanItem[];
}

export interface MissingDataQuestion {
  id: string;
  question: string;
  why_it_matters: string;
  target_field?: string;
}

export interface MissingDataQuestionsBlock {
  type: "missing_data_questions";
  title: string;
  summary: string;
  questions: MissingDataQuestion[];
}

export interface SafetyEscalationBlock {
  type: "safety_escalation";
  title: string;
  severity: "high" | "medium";
  content: string;
  reasons: string[];
}

export interface SdkReasoningBlock {
  type: "sdk_reasoning";
  title: string;
  summary: string;
  model?: string;
  effort?: string;
}

export type AiBlock =
  | SdkReasoningBlock
  | SimpleAnswerBlock
  | ReasoningBlock
  | ActionPlanBlock
  | MissingDataQuestionsBlock
  | SafetyEscalationBlock;

export type AiMessageType = "simple_answer" | "deep_dive" | "safety_escalation";

export interface AiChatResponse {
  message_type: AiMessageType;
  blocks: AiBlock[];
}

type AiStreamEvent =
  | {
      event: "message_started";
      type: "message_started";
      data: { message_type: AiMessageType };
    }
  | {
      event: "block_started";
      type: "block_started";
      data: { index: number; block: AiBlock };
    }
  | {
      event: "block_delta";
      type: "block_delta";
      data: { index: number; path: string; delta: string };
    }
  | {
      event: "block_patch";
      type: "block_patch";
      data: { index: number; patch: Record<string, unknown> };
    }
  | {
      event: "block_item_appended";
      type: "block_item_appended";
      data: { index: number; path: string; item: unknown };
    }
  | {
      event: "block_finished";
      type: "block_finished";
      data: { index: number };
    }
  | {
      event: "message_finished";
      type: "message_finished";
      data: AiChatResponse;
    };

type StreamUpdate = {
  isFinal: boolean;
  response: AiChatResponse;
};

type AiChatContextValue = {
  messages: AiConversationMessage[];
  isLoading: boolean;
  sendMessage: (
    userMessage: string,
    options?: AiChatSendOptions,
  ) => Promise<void>;
  reset: () => void;
};

const AiChatContext = createContext<AiChatContextValue | null>(null);

function buildWebSocketUrl(
  tenantId: string,
  chatId: string,
  jwt: string,
  websocketUrl?: string,
) {
  const baseTarget =
    websocketUrl ?? `${API_BASE_URL}/${tenantId}/ai/chats/${chatId}`;
  const url = new URL(baseTarget, API_BASE_URL);

  if (url.protocol === "http:") {
    url.protocol = "ws:";
  } else if (url.protocol === "https:") {
    url.protocol = "wss:";
  }

  url.searchParams.set("token", jwt);

  return url.toString();
}

function extractChatSession(raw: unknown): ChatSession {
  const obj = raw as Record<string, unknown> | undefined;
  const data = (obj?.data as Record<string, unknown> | undefined) ?? obj;
  const chatId = data?.chatId ?? data?.chat_id ?? data?.id;

  if (!chatId) {
    throw new Error("AI chat creation did not return a chat id");
  }

  return {
    chatId: chatId as string,
    websocketUrl: (data?.websocketUrl ??
      data?.websocket_url ??
      data?.socketUrl ??
      data?.socket_url) as string | undefined,
  };
}

function extractAiChatResponse(raw: unknown): AiChatResponse | null {
  const candidates = [
    raw,
    (raw as Record<string, unknown>)?.data,
    (raw as Record<string, unknown>)?.payload,
    (raw as Record<string, unknown>)?.message,
  ];

  for (const candidate of candidates) {
    if (
      candidate &&
      typeof candidate === "object" &&
      "message_type" in candidate &&
      Array.isArray((candidate as Record<string, unknown>).blocks)
    ) {
      return candidate as AiChatResponse;
    }
  }

  return null;
}

function toConversationMessages(
  messages: AiChatDetail["messages"],
): AiConversationMessage[] {
  const restoredMessages: AiConversationMessage[] = [];

  for (const message of messages) {
    if (message.role === "user") {
      restoredMessages.push({ role: "user", text: message.content });
      continue;
    }

    if (!message.response) {
      continue;
    }

    restoredMessages.push({
      role: "assistant",
      thinking: false,
      response: message.response,
    });
  }

  return restoredMessages;
}

function extractStreamEvent(raw: unknown): AiStreamEvent | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }

  const event = (raw as Record<string, unknown>)?.event;
  const type = (raw as Record<string, unknown>)?.type;

  if (event !== type) {
    return null;
  }

  switch (event) {
    case "message_started":
    case "block_started":
    case "block_delta":
    case "block_patch":
    case "block_item_appended":
    case "block_finished":
    case "message_finished":
      return raw as AiStreamEvent;
    default:
      return null;
  }
}

function extractSocketError(raw: unknown): string | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }

  const obj = raw as Record<string, unknown>;
  const dataVal = obj.data as Record<string, unknown> | undefined;
  const data = dataVal ?? obj;
  const errorObj = data.error as Record<string, unknown> | string | undefined;
  const rawErrorObj = obj.error as Record<string, unknown> | string | undefined;

  const nestedError =
    (typeof errorObj === "object" ? errorObj?.message : undefined) ??
    errorObj ??
    (typeof rawErrorObj === "object" ? rawErrorObj?.message : undefined) ??
    rawErrorObj;

  if (typeof nestedError === "string" && nestedError.length > 0) {
    return nestedError;
  }

  if ((data.type ?? obj.type) === "error") {
    return (
      (data.message as string | undefined) ??
      (obj.message as string | undefined) ??
      "AI chat failed"
    );
  }

  return null;
}

function normalizeAiChatError(error: unknown): Error {
  const message = error instanceof Error ? error.message : "";

  if (message === "Failed to fetch" || message === "Network request failed") {
    return new Error("AI chat service is unreachable");
  }

  return error instanceof Error ? error : new Error("AI chat request failed");
}

function toPathSegment(segment: string): string | number {
  return /^\d+$/.test(segment) ? Number(segment) : segment;
}

function appendDeltaAtPath<T extends object>(
  value: T,
  path: string,
  delta: string,
): T {
  const segments = path.split(".").map(toPathSegment);
  const root = Array.isArray(value) ? [...value] : { ...value };

  if (segments.length === 0) {
    return root as T;
  }

  let nextCursor: Record<string | number, unknown> = root as Record<
    string | number,
    unknown
  >;
  let currentCursor: Record<string | number, unknown> = value as Record<
    string | number,
    unknown
  >;

  for (let index = 0; index < segments.length - 1; index += 1) {
    const segment = segments[index];
    if (segment === undefined) {
      return root as T;
    }
    const currentValue = currentCursor?.[segment];
    const clonedValue = Array.isArray(currentValue)
      ? [...currentValue]
      : { ...(currentValue ?? {}) };

    nextCursor[segment] = clonedValue;
    nextCursor = clonedValue;
    currentCursor = (currentValue ?? {}) as Record<string | number, unknown>;
  }

  const finalSegment = segments[segments.length - 1];
  if (finalSegment === undefined) {
    return root as T;
  }
  const existingValue = currentCursor?.[finalSegment];
  nextCursor[finalSegment] =
    `${typeof existingValue === "string" ? existingValue : ""}${delta}`;

  return root as T;
}

function dedupeBlocks(blocks: AiBlock[]): AiBlock[] {
  const seen = new Set<string>();
  return blocks.filter((block) => {
    const key = `${block.type}:${"title" in block ? block.title : ""}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function normalizeStreamBlock(block: AiBlock): AiBlock {
  switch (block.type) {
    case "sdk_reasoning":
      return {
        ...block,
        title: block.title || "",
        summary: block.summary || "",
      };
    case "simple_answer":
      return {
        ...block,
        title: block.title || "",
        content: block.content || "",
        sources: block.sources || [],
      };
    case "reasoning":
      return {
        ...block,
        title: block.title || "",
        summary: block.summary || "",
        categories: block.categories || [],
        sources: block.sources || [],
      };
    case "action_plan":
      return {
        ...block,
        title: block.title || "",
        summary: block.summary || "",
        items: block.items || [],
      };
    case "missing_data_questions":
      return {
        ...block,
        title: block.title || "",
        summary: block.summary || "",
        questions: block.questions || [],
      };
    case "safety_escalation":
      return {
        ...block,
        title: block.title || "",
        content: block.content || "",
        severity: block.severity || "medium",
        reasons: block.reasons || [],
      };
  }
}

function applyStreamEvent(
  currentResponse: AiChatResponse | null,
  raw: unknown,
): StreamUpdate | null {
  const streamEvent = extractStreamEvent(raw);

  if (!streamEvent) {
    return null;
  }

  switch (streamEvent.event) {
    case "message_started":
      return {
        isFinal: false,
        response: {
          message_type: streamEvent.data.message_type,
          blocks: [],
        },
      };
    case "block_started": {
      if (!currentResponse) {
        return null;
      }

      const nextBlocks = [...currentResponse.blocks];
      nextBlocks[streamEvent.data.index] = normalizeStreamBlock(
        streamEvent.data.block,
      );

      return {
        isFinal: false,
        response: { ...currentResponse, blocks: nextBlocks },
      };
    }
    case "block_delta": {
      if (!currentResponse) {
        return null;
      }

      const block = currentResponse.blocks[streamEvent.data.index];
      if (!block) {
        return null;
      }

      const nextBlocks = [...currentResponse.blocks];
      nextBlocks[streamEvent.data.index] = appendDeltaAtPath(
        block,
        streamEvent.data.path,
        streamEvent.data.delta,
      );

      return {
        isFinal: false,
        response: { ...currentResponse, blocks: nextBlocks },
      };
    }
    case "block_patch": {
      if (!currentResponse) {
        return null;
      }

      const block = currentResponse.blocks[streamEvent.data.index];
      if (!block) {
        return null;
      }

      const nextBlocks = [...currentResponse.blocks];
      nextBlocks[streamEvent.data.index] = {
        ...block,
        ...streamEvent.data.patch,
      } as AiBlock;

      return {
        isFinal: false,
        response: { ...currentResponse, blocks: nextBlocks },
      };
    }
    case "block_item_appended": {
      if (!currentResponse) {
        return null;
      }

      const block = currentResponse.blocks[streamEvent.data.index];
      if (!block) {
        return null;
      }

      const { path, item } = streamEvent.data;
      const existing = (block as unknown as Record<string, unknown>)[path];
      const arr = Array.isArray(existing) ? existing : [];

      const nextBlocks = [...currentResponse.blocks];
      nextBlocks[streamEvent.data.index] = {
        ...block,
        [path]: [...arr, item],
      } as AiBlock;

      return {
        isFinal: false,
        response: { ...currentResponse, blocks: nextBlocks },
      };
    }
    case "block_finished":
      return currentResponse
        ? {
            isFinal: false,
            response: currentResponse,
          }
        : null;
    case "message_finished":
      return {
        isFinal: true,
        response: streamEvent.data,
      };
  }
}

function useAiChatTransport() {
  const stytch = useStytch();
  const { client, patientId } = useApiClient();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chatSessionRef = useRef<ChatSession | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const socketPromiseRef = useRef<Promise<WebSocket> | null>(null);
  const pendingRequestRef = useRef<PendingRequest | null>(null);
  const streamingResponseRef = useRef<AiChatResponse | null>(null);

  const cleanupSocket = useCallback(() => {
    const pending = pendingRequestRef.current;
    if (pending) {
      clearTimeout(pending.timeoutId);
      pending.reject(new Error("AI chat connection closed"));
      pendingRequestRef.current = null;
    }

    socketRef.current?.close();
    socketRef.current = null;
    socketPromiseRef.current = null;
  }, []);

  const restoreHistory = useCallback(async (): Promise<
    AiConversationMessage[]
  > => {
    cleanupSocket();
    chatSessionRef.current = null;
    streamingResponseRef.current = null;

    if (!patientId) {
      return [];
    }

    const { data: chatList } = await client.GET("/ai/chats");
    if (!chatList) {
      return [];
    }

    // The current Moon surface represents one ongoing conversation. Restore
    // the newest chat that has messages, skipping any empty shells created by
    // an earlier interrupted session.
    const chats = chatList.data;
    for (const chat of chats) {
      const { data: chatDetail } = await client.GET("/ai/chats/{chatId}", {
        params: { path: { chatId: chat.id } },
      });

      if (!chatDetail) {
        continue;
      }

      const restoredMessages = toConversationMessages(chatDetail.data.messages);
      if (restoredMessages.length === 0) {
        continue;
      }

      chatSessionRef.current = { chatId: chatDetail.data.id };
      return restoredMessages;
    }

    return [];
  }, [cleanupSocket, client, patientId]);

  useEffect(() => () => cleanupSocket(), [cleanupSocket]);

  const sendMessage = useCallback(
    async (
      userMessage: string,
      onStream?: (response: AiChatResponse) => void,
      options?: { actionItemId?: string; actionItemContext?: string },
    ): Promise<AiChatResponse> => {
      const jwt = stytch.session.getTokens()?.session_jwt;
      const { tenantId } = getIdsFromMetadata(
        stytch.user.getSync()?.trusted_metadata ?? {},
      );

      if (!jwt) {
        throw new Error("Not authenticated");
      }

      setIsLoading(true);
      setError(null);
      streamingResponseRef.current = null;

      try {
        if (!chatSessionRef.current) {
          const controller = new AbortController();
          const timeoutId = setTimeout(
            () => controller.abort(),
            CHAT_SESSION_TIMEOUT_MS,
          );
          let response: Response;

          try {
            response = await fetch(`${API_BASE_URL}/${tenantId}/ai/chats`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${jwt}`,
              },
              signal: controller.signal,
            });
          } catch (error) {
            if (error instanceof Error && error.name === "AbortError") {
              throw new Error("AI chat session timed out");
            }
            throw error;
          } finally {
            clearTimeout(timeoutId);
          }

          if (!response.ok) {
            throw new Error(`Chat creation failed: ${response.status}`);
          }

          chatSessionRef.current = extractChatSession(await response.json());
        }

        if (!socketPromiseRef.current) {
          socketPromiseRef.current = new Promise<WebSocket>(
            (resolve, reject) => {
              const session = chatSessionRef.current;

              if (!session) {
                reject(new Error("AI chat session is missing"));
                return;
              }

              const socket = new WebSocket(
                buildWebSocketUrl(
                  tenantId,
                  session.chatId,
                  jwt,
                  session.websocketUrl,
                ),
              );
              let settled = false;
              const connectTimeoutId = setTimeout(() => {
                if (settled) return;
                settled = true;
                socketPromiseRef.current = null;
                socket.close();
                reject(new Error("AI chat connection timed out"));
              }, CHAT_SESSION_TIMEOUT_MS);

              socket.onopen = () => {
                if (settled) return;
                settled = true;
                clearTimeout(connectTimeoutId);
                socketRef.current = socket;
                resolve(socket);
              };

              socket.onerror = () => {
                if (settled) return;
                settled = true;
                clearTimeout(connectTimeoutId);
                socketPromiseRef.current = null;
                socket.close();
                reject(new Error("AI chat connection failed"));
              };

              socket.onclose = () => {
                clearTimeout(connectTimeoutId);
                socketRef.current = null;
                socketPromiseRef.current = null;

                if (!settled) {
                  settled = true;
                  reject(new Error("AI chat connection closed"));
                  return;
                }

                const pending = pendingRequestRef.current;
                if (pending) {
                  clearTimeout(pending.timeoutId);
                  pending.reject(new Error("AI chat connection closed"));
                  pendingRequestRef.current = null;
                }
              };

              socket.onmessage = (event) => {
                let payload: unknown;

                try {
                  payload = JSON.parse(String(event.data));
                } catch {
                  return;
                }

                const pending = pendingRequestRef.current;
                if (!pending) {
                  return;
                }

                const socketError = extractSocketError(payload);
                if (socketError) {
                  clearTimeout(pending.timeoutId);
                  pending.reject(new Error(socketError));
                  pendingRequestRef.current = null;
                  return;
                }

                const streamUpdate = applyStreamEvent(
                  streamingResponseRef.current,
                  payload,
                );

                if (streamUpdate) {
                  // Reset timeout on every successful stream event
                  clearTimeout(pending.timeoutId);
                  pending.timeoutId = setTimeout(() => {
                    pendingRequestRef.current = null;
                    pending.reject(new Error("AI chat timed out"));
                  }, CHAT_RESPONSE_TIMEOUT_MS);

                  const dedupedResponse = {
                    ...streamUpdate.response,
                    blocks: dedupeBlocks(streamUpdate.response.blocks),
                  };
                  streamingResponseRef.current = dedupedResponse;
                  pending.onStream?.(dedupedResponse);

                  if (streamUpdate.isFinal) {
                    clearTimeout(pending.timeoutId);
                    pending.resolve(dedupedResponse);
                    pendingRequestRef.current = null;
                  }

                  return;
                }

                const aiResponse = extractAiChatResponse(payload);
                if (!aiResponse) {
                  return;
                }

                clearTimeout(pending.timeoutId);
                pending.resolve(aiResponse);
                pendingRequestRef.current = null;
              };
            },
          );
        }

        const socket = await socketPromiseRef.current;
        const data = await new Promise<AiChatResponse>((resolve, reject) => {
          const timeoutId = setTimeout(() => {
            pendingRequestRef.current = null;
            reject(new Error("AI chat timed out"));
          }, CHAT_RESPONSE_TIMEOUT_MS);

          pendingRequestRef.current = {
            resolve,
            reject,
            timeoutId,
            onStream,
          };

          try {
            socket.send(
              JSON.stringify({
                message: userMessage,
                ...(options?.actionItemId
                  ? { actionItemId: options.actionItemId }
                  : {}),
                ...(options?.actionItemContext
                  ? { actionItemContext: options.actionItemContext }
                  : {}),
              }),
            );
          } catch (error) {
            clearTimeout(timeoutId);
            pendingRequestRef.current = null;
            reject(error);
          }
        });

        return data;
      } catch (err) {
        const normalizedError = normalizeAiChatError(err);
        const message = normalizedError.message;
        setError(message);
        throw normalizedError;
      } finally {
        setIsLoading(false);
      }
    },
    [stytch],
  );

  const reset = useCallback(() => {
    setError(null);
    chatSessionRef.current = null;
    streamingResponseRef.current = null;
    cleanupSocket();
  }, [cleanupSocket]);

  return { sendMessage, isLoading, error, patientId, reset, restoreHistory };
}

export function AiChatProvider({ children }: { children: ReactNode }) {
  const t = useTranslations();
  const fixture = getAiChatFixture();
  const {
    sendMessage: sendTransportMessage,
    isLoading: transportIsLoading,
    patientId,
    reset: resetTransport,
    restoreHistory,
  } = useAiChatTransport();
  const [messages, setMessages] = useState<AiConversationMessage[]>(
    () => fixture?.messages ?? [],
  );
  const [fixtureLoading, setFixtureLoading] = useState(
    () => fixture?.isLoading ?? false,
  );
  const fixtureIsActive = fixture !== null;
  const [historyLoading, setHistoryLoading] = useState(!fixtureIsActive);
  const activePatientIdRef = useRef(patientId);
  if (activePatientIdRef.current !== patientId) {
    activePatientIdRef.current = patientId;
  }
  const isLoading = transportIsLoading || fixtureLoading || historyLoading;

  useEffect(() => {
    if (fixtureIsActive) {
      setHistoryLoading(false);
      return;
    }

    let cancelled = false;
    setHistoryLoading(true);
    setMessages([]);

    void restoreHistory()
      .then((restoredMessages) => {
        if (!cancelled) {
          setMessages(restoredMessages);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          console.warn(
            "[AiChatProvider] Could not restore chat history:",
            error instanceof Error ? error.message : "unknown error",
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setHistoryLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [fixtureIsActive, restoreHistory]);

  const sendMessage = useCallback(
    async (userMessage: string, options?: AiChatSendOptions) => {
      const text = userMessage.trim();
      if (!text || isLoading) return;
      const requestPatientId = patientId;

      if (fixtureIsActive && fixture) {
        setMessages((prev) => [
          ...prev,
          { role: "user", text, quote: options?.quote },
          { role: "assistant", thinking: true, response: null },
        ]);
        setFixtureLoading(true);
        setTimeout(() => {
          if (activePatientIdRef.current !== requestPatientId) return;

          setMessages((prev) => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last?.role === "assistant") {
              updated[updated.length - 1] = {
                ...last,
                thinking: false,
                response: fixture.response,
              };
            }
            return updated;
          });
          setFixtureLoading(false);
        }, 650);
        return;
      }

      setMessages((prev) => [
        ...prev,
        { role: "user", text, quote: options?.quote },
        { role: "assistant", thinking: true, response: null },
      ]);

      try {
        const response = await sendTransportMessage(
          text,
          (partialResponse) => {
            if (activePatientIdRef.current !== requestPatientId) return;

            setMessages((prev) => {
              const updated = [...prev];
              const last = updated[updated.length - 1];
              if (last?.role === "assistant") {
                updated[updated.length - 1] = {
                  ...last,
                  thinking: true,
                  response: partialResponse,
                };
              }
              return updated;
            });
          },
          options
            ? {
                actionItemId: options.actionItemId,
                actionItemContext: options.actionItemContext,
              }
            : undefined,
        );

        if (activePatientIdRef.current !== requestPatientId) return;

        setMessages((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last?.role === "assistant") {
            updated[updated.length - 1] = {
              ...last,
              thinking: false,
              response,
            };
          }
          return updated;
        });
      } catch (err) {
        if (activePatientIdRef.current !== requestPatientId) return;

        const errMsg =
          err instanceof Error
            ? err.message
            : t("learn.ai_assistant.error_generic");
        console.error("[AiChatProvider] sendMessage failed:", errMsg);
        const fallback: AiChatResponse = {
          message_type: "simple_answer",
          blocks: [
            {
              type: "simple_answer",
              title: t("learn.ai_assistant.connection_error_title"),
              content: t("learn.ai_assistant.connection_error_message"),
              sources: [],
            },
          ],
        };
        setMessages((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last?.role === "assistant") {
            updated[updated.length - 1] = {
              ...last,
              thinking: false,
              response: fallback,
            };
          }
          return updated;
        });
      }
    },
    [fixture, fixtureIsActive, isLoading, patientId, sendTransportMessage, t],
  );

  const reset = useCallback(() => {
    setMessages(fixture?.messages ?? []);
    setFixtureLoading(fixture?.isLoading ?? false);
    setHistoryLoading(false);
    resetTransport();
  }, [fixture, resetTransport]);

  return createElement(
    AiChatContext.Provider,
    { value: { messages, isLoading, sendMessage, reset } },
    children,
  );
}

export function useAiChat() {
  const context = useContext(AiChatContext);
  if (!context) {
    throw new Error("useAiChat must be used within AiChatProvider");
  }
  return context;
}
